import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import type { RootState } from 'store'
import {
  NavigateBackward,
  NavigateForward,
  UpdateUserValue,
  SetCardData,
  SetLoading
} from 'store/userSetup/action'
import handleApiCall from 'api/handleApiCall'

import { handleToastMessage } from 'utils/handleToastMesasge'

import TellitInput from 'components/form/TellitInput'

import { BackBtn, Header, NextBtn } from './subComponents'
import TellitLoader from 'components/common/TellitLoader'
import useValidateForm, { type IErrorObj } from 'utils/useFormValidate'
import useErrorArray from 'utils/useErrorArray'
import { getErrorInField, notNullUndefinedCheck } from 'utils/commonFunctions'
import CheckPersonNumber from 'components/common/CheckPersonNumber'
import type { federatedLoginTypes } from 'store/userSetup/types'
import NumberBadge from 'components/common/TellitNumberBadge'
import { Base64 } from 'js-base64'
import TellitButton from 'components/common/TellitButton'
import BankIdLogo from 'components/BankIdLogo'

const searchParams = new URLSearchParams(window.location.search)
const override = searchParams.get('override')

type DataItem = Record<string, string>

const StepTwo = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const formValues = useSelector(
    (state: RootState) => state.userSetup.userValues
  )
  const [loadingCreditSafeCheck, setLoadingCreditSafeCheck] =
    useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorArray, setErrorArray] = useState<IErrorObj[]>([])
  const [creditSafePersonalNumberError, setCreditSafePersonalNumberError] =
    useState<string>('')
  const [showHelp, setShowHelp] = useState<boolean>(false)

  const [federated, setFederated] = useState<federatedLoginTypes>({})
  const [startPolling, setStartPolling] = useState<boolean>(false)
  const [openBankIDUrl, setOpenBankIDUrl] = useState<string>('')

  const [width, setWidth] = useState<number>(window.innerWidth)

  const handleOnChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): JSX.Element | null => {
    const { name, value } = e.target
    dispatch(UpdateUserValue({ name, value }))
    return null
  }

  function creditCheck(): void {
    setFederated({})
    setCreditSafePersonalNumberError('')

    // Comment this earlier PNR check after we implement new credisafe check
    // const isPersonNumberMatch = CheckPersonNumber({
    //   personNumber: ceoPNR?.trim(),
    //   comparingNumber: formValues?.personNumber.trim()
    // })
    // if (isPersonNumberMatch) {
    setLoadingCreditSafeCheck(true)
    void handleApiCall({
      urlType: 'checkCreditSafe',
      urlParams: `?orgnr=${
        formValues.orgNumber
      }&override=${override}&companyname=${formValues.companyName}&pnr=${
        formValues.personNumber
      }&data=%7B"namn":"${formValues.name.trim()}","mobilnummer":"${formValues.mobile.trim()}","e-post":"${formValues.email.trim()}","rating":${rating}%7D`,
      cb: (res, state) => {
        if (state) {
          if (res?.data !== undefined) {
            setLoadingCreditSafeCheck(false)
            dispatch(
              UpdateUserValue({
                name: 'companyName',
                value: res?.data.companyName
              })
            )
            dispatch(
              UpdateUserValue({
                name: 'address',
                value: res?.data.address
              })
            )
            dispatch(
              UpdateUserValue({
                name: 'zip',
                value: res?.data.zip
              })
            )
            dispatch(
              UpdateUserValue({
                name: 'city',
                value: res?.data.town
              })
            )
            localStorage.setItem('RATING', res?.data.rating)
            dispatch(NavigateForward())
            dispatch(SetLoading({ name: 'loading', value: true }))
            void handleApiCall({
              urlType: 'areaCodes',
              // setLoading: setAreaCodeLoading,
              cb: (res: any, state) => {
                dispatch(
                  SetLoading({
                    name: 'loading',
                    value: false
                  })
                )
                if (state) {
                  try {
                    const transformedArray = res?.data?.map(
                      (item: DataItem) => {
                        const [value, name] = Object.entries(item)[0]
                        const nameCustom = `${value} - ${name}`
                        return { value, name: nameCustom }
                      }
                    )

                    const value: any = transformedArray
                    dispatch(
                      SetCardData({
                        name: 'areaCodes',
                        value
                      })
                    )
                  } catch (error) {
                    console.log('error333333', error)
                    throw error
                  }
                } else {
                  if (res.message !== undefined && res.message !== '') {
                    handleToastMessage(res.message)
                  }
                  console.log('error')
                }
              }
            })
          } else {
            setShowHelp(true)
            setLoadingCreditSafeCheck(false)
            setCreditSafePersonalNumberError(res?.message)
          }
        } else {
          setShowHelp(true)
          setLoadingCreditSafeCheck(false)
          setCreditSafePersonalNumberError(res?.message)
        }
      }
    })
  }

  // const ceoPNR = localStorage.getItem('CEO_PNR')
  const rating = localStorage.getItem('RATING')

  const forms = [
    {
      label: t('StepTwo.Form.PersonalNumber.label'),
      id: 'personNumber',
      name: 'personNumber',
      value: formValues.personNumber,
      type: 'text',
      // placeholder: t('StepTwo.Form.PersonalNumber.Placeholder'),
      required: true,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length > 10 || isNaN(Number(e.target.value))) {
          return
        }
        handleOnChange?.(e)
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {},
      enableErrorMsg: true,
      validationType: 'personalNumber',
      errorMsg: ''
    },
    {
      label: t('StepTwo.Form.Name.Label'),
      id: 'name',
      name: 'name',
      value: formValues.name,
      type: 'text',
      // placeholder: t('StepTwo.Form.Name.Placeholder'),
      required: true,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        handleOnChange?.(e)
      }
    },
    {
      label: t('StepTwo.Form.Mobile.Label'),
      id: 'mobile',
      name: 'mobile',
      value: formValues.mobile,
      type: 'text',
      // placeholder: '000000 000000',
      required: true,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length > 10 || isNaN(Number(e.target.value))) {
          return
        }
        handleOnChange?.(e)
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {},
      enableErrorMsg: true,
      validationType: 'mobile'
    },
    {
      label: t('StepTwo.Form.Email.Label'),
      id: 'email',
      name: 'email',
      value: formValues.email,
      type: 'email',
      // placeholder: 'jane@gmail.com',
      required: true,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        handleOnChange?.(e)
      },
      enableErrorMsg: true
    }
  ]

  useErrorArray({
    errorArray,
    setErrorArray,
    setIsError,
    step: 2
  })

  function handleWindowSizeChange(): void {
    setWidth(window.innerWidth)
  }
  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange)
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [])

  const controller = new AbortController()

  const handleGetSession = (sessionId: string): void => {
    void handleApiCall({
      urlType: 'getSession',
      urlParams: `?grandidsession=${sessionId}&onboarding_sign`,
      signal: controller.signal,
      cb: (res, state) => {
        if (state) {
          if (res?.data?.grandidObject !== undefined) {
            if (res?.data?.grandidObject.message.hintCode !== 'userSign') {
              setOpenBankIDUrl(
                `bankid:///?autostarttoken=${res?.data?.grandidObject.autoStartToken}&redirect=null`
              )
              setFederated({
                ...federated,
                QRCode: res?.data?.grandidObject.QRCode,
                status: ''
              })
            } else {
              setFederated({
                ...federated,
                status: 'pause'
              })
            }
          }

          // cancel api call when success
          if (res?.data?.count === 1) {
            if (res?.data?.status === 'success' && res?.data?.count === 1) {
              setStartPolling(false)
              controller.abort()

              if (res?.data?.personal_number !== undefined) {
                const comparingNumber =
                  override?.trim() !== undefined
                    ? override.trim()
                    : formValues.personNumber.toString()
                const isPersonNumberMatch = CheckPersonNumber({
                  personNumber: res?.data?.personal_number.toString(),
                  comparingNumber
                })
                if (!isPersonNumberMatch) {
                  handleToastMessage(t('ErrorMsg.Form.PersonalNumberMismatch'))
                  setFederated({})
                } else {
                  setTimeout(() => {
                    creditCheck()
                  }, 1000)
                }
              } else {
                setTimeout(() => {
                  creditCheck()
                }, 1000)
              }
            }
          } else {
            if (res?.data?.status === 'success') {
              setStartPolling(false)
              controller.abort()
              if (res?.data?.personal_number !== undefined) {
                const comparingNumber =
                  override?.trim() !== undefined
                    ? override.trim()
                    : formValues.personNumber.toString()
                const isPersonNumberMatch = CheckPersonNumber({
                  personNumber: res?.data?.personal_number.toString(),
                  comparingNumber
                })
                if (!isPersonNumberMatch) {
                  handleToastMessage(t('ErrorMsg.Form.PersonalNumberMismatch'))
                  setFederated({})
                } else {
                  setTimeout(() => {
                    creditCheck()
                  }, 1000)
                }
              } else {
                setTimeout(() => {
                  creditCheck()
                }, 1000)
              }
            }
          }
        } else {
          setStartPolling(false)
          if (notNullUndefinedCheck(res.message)) {
            handleToastMessage(res.message)
          }
          // handleSaveValues()
          setTimeout(() => {
            setFederated((previous) => {
              return {
                ...previous,
                QRCode: undefined
              }
            })
          }, 2000)
          console.log('error')
        }
      }
    })
  }

  useEffect(() => {
    if (startPolling) {
      const pollInterval = 2000 // 3 seconds
      const getSessionInterval = setInterval(() => {
        if (federated?.sessionId != null) {
          handleGetSession(federated?.sessionId)
        }
      }, pollInterval)
      return () => {
        clearInterval(getSessionInterval)
      }
    }
  }, [startPolling])

  const isMobile = width <= 768

  const handleSubmit = (): void => {
    // bank id verify
    void handleApiCall({
      urlType: 'federatedLogin',
      urlParams: `?override=${override}`,
      setLoading(state) {
        setLoadingCreditSafeCheck(state)
      },
      cb: (res, state) => {
        if (state) {
          setFederated(res?.data)
          setTimeout(() => {
            if (res?.data?.sessionId !== undefined) {
              setStartPolling(true)
              handleGetSession(res?.data?.sessionId)
            }
          }, 1000)
        } else {
          console.log('error')
          setStartPolling(false)
          setFederated({})
          if (notNullUndefinedCheck(res.message)) {
            handleToastMessage(res.message)
          }
        }
      }
    })
  }

  return (
    <TellitLoader
      loading={loadingCreditSafeCheck}
      loadingText="Kontrollerar kreditupplysning"
    >
      {federated?.QRCode !== undefined ? (
        <>
          <div className="mt-8 lg:px-sp9 step-6-padding">
            <div className="mb-3 flex flex-col flex-wrap justify-center w-full">
              <div className="items-center flex mb-3">
                <NumberBadge count={1} />
                <span className="text-black">
                  {t('StepSix.Card.Qr.StepOne')}
                </span>
              </div>
              <div className="flex items-center mb-3">
                <NumberBadge count={2} />
                <span className="text-black">
                  {t('StepSix.Card.Qr.StepTwo')}
                </span>
              </div>
              <div className="flex items-center mb-3">
                <NumberBadge count={3} />
                <span className="text-black">
                  {t('StepSix.Card.Qr.StepThree')}
                </span>
              </div>
              {federated.status !== 'pause' ? (
                <>
                  <div
                    className={`mx-auto mt-4`}
                    dangerouslySetInnerHTML={
                      federated.QRCode != null
                        ? { __html: Base64.decode(federated.QRCode) }
                        : { __html: '' }
                    }
                  />
                  {isMobile && (
                    <TellitButton
                      href={openBankIDUrl}
                      disabled={false}
                      customClass={`disabled:bg-grey disabled:hover:bg-grey bg-secondary group flex items-center flex-row-reverse shadow-buttonSecondary justify-between px-[27px] rounded-4xl lg:h-[56px] hover:bg-primary`}
                      label={t('StepSix.Label.ÖppnaBankID')}
                      onClick={() => {}}
                      icon={<BankIdLogo />}
                    />
                  )}
                </>
              ) : (
                <div
                  className={`flex flex-col justify-center items-center h-[20rem] text-black`}
                >
                  {/* <div>{View}</div> */}
                  <span className="loading loading-spinner loading-lg bg-secondary" />{' '}
                  <br />
                  Bekräfta med BankID
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {!showHelp &&
            forms.map((formItem, idx) => (
              <TellitInput
                key={`${idx}_formItem`}
                label={formItem.label}
                id={formItem.id}
                name={formItem.name}
                value={formItem.value}
                alignCenter
                type={formItem.type}
                onChange={formItem.onChange}
                onBlur={formItem.onBlur}
                enableErrorMsg={formItem.enableErrorMsg}
                validationType={formItem.validationType}
                setError={(val) => {
                  const isFormError = useValidateForm({
                    fieldError: val,
                    errorArray,
                    name: formItem.name,
                    setErrorArray
                  })
                  setIsError(isFormError)
                }}
                required={formItem.required}
                errorMsg={formItem.errorMsg}
                triggerValidationOnMount={getErrorInField(
                  formItem.name,
                  errorArray
                )}
              />
            ))}

          {creditSafePersonalNumberError !== '' && (
            <p className="text-center text-error text-[0.8rem] md:text-base-20 font-normal mt-sp3 mb-[-1rem]">
              {creditSafePersonalNumberError}
            </p>
          )}

          {showHelp && (
            <>
              <Header className="px-10">
                <>
                  {t('StepOne.CreditsafeError.Label1')}
                  <br></br>
                  <br></br>
                  {t('StepOne.CreditsafeError.Label2')}{' '}
                  <span className="text-secondary">020-120 28 82</span>.
                </>
              </Header>
              <div
                className={`self-center ${
                  creditSafePersonalNumberError !== '' ? '' : 'mt-6'
                } flex flex-col xl:flex-row flex-wrap justify-center items-center w-full`}
              >
                <NextBtn
                  label={t('StepTwo.Button.TryAgain')}
                  type="up"
                  handleClick={() => {
                    // dispatch(UpdateUserValue({ name: 'orgNumber', value: '' }))
                    // dispatch(UpdateUserValue({ name: 'companyName', value: '' }))
                    setCreditSafePersonalNumberError('')
                    setShowHelp(false)
                  }}
                />
              </div>
            </>
          )}

          {!showHelp && (
            <div className="self-center mt-8 2xl:pt-[0px] flex flex-row flex-nowrap justify-between items-center gap-4 w-full 2xl:px-sp100 step-2-footer">
              <BackBtn
                handleClick={() => {
                  dispatch(NavigateBackward())
                }}
                customClass="2xl:pl-0"
              />
              <NextBtn
                label={t('StepSix.Card.Button.Submit')}
                handleClick={() => {
                  handleSubmit()
                }}
                disabled={
                  formValues.personNumber === '' ||
                  formValues.name === '' ||
                  formValues.mobile === '' ||
                  formValues.email === '' ||
                  isError
                }
              />
            </div>
          )}
        </>
      )}
    </TellitLoader>
  )
}

export default StepTwo
